import { PageSpeedResult } from '../types';
import { safeToFixed, getImportantOpportunities } from './dataTransformers';
import { calculateMobileRating, calculateDesktopRating, getRatingGrade, getRatingColor, SiteData } from './overallRating';

// 기회 제목 번역 함수
export const translateOpportunityTitle = (title: string): string => {
  const translations: { [key: string]: string } = {
    'Remove unused CSS': '사용하지 않는 CSS 제거',
    'Remove unused JavaScript': '사용하지 않는 JavaScript 제거',
    'Reduce unused CSS': '미사용 CSS 줄이기',
    'Reduce unused JavaScript': '미사용 JavaScript 줄이기',
    'Eliminate render-blocking resources': '렌더링 차단 리소스 제거',
    'Serve images in next-gen formats': '차세대 이미지 포맷 사용',
    'Minify CSS': 'CSS 압축',
    'Minify JavaScript': 'JavaScript 압축',
    'Enable text compression': '텍스트 압축 활성화',
    'Properly size images': '이미지 크기 최적화',
    'Defer offscreen images': '화면 밖 이미지 지연 로딩',
    'Avoid serving legacy JavaScript to modern browsers': '모던 브라우저에 레거시 JavaScript 제공 방지',
    'Reduce server response times (TTFB)': '서버 응답 시간 단축 (TTFB)',
    'Avoid an excessive DOM size': '과도한 DOM 크기 방지',
    'Minimize main-thread work': '메인 스레드 작업 최소화',
    'Reduce JavaScript execution time': 'JavaScript 실행 시간 단축',
    'Avoid large layout shifts': '큰 레이아웃 이동 방지',
    'Use efficient cache policy on static assets': '정적 자산에 효율적인 캐시 정책 사용',
    'Avoid chaining critical requests': '중요한 요청 체이닝 방지',
    'Keep request counts low and transfer sizes small': '요청 수를 적게 유지하고 전송 크기 축소',
    'Avoid multiple page redirects': '여러 페이지 리다이렉트 방지',
    'Efficiently encode images': '이미지 효율적 인코딩'
  };
  
  return translations[title] || title;
};

// 마크다운 링크를 HTML 링크로 변환하는 함수
const convertMarkdownLinksToHtml = (text: string): string => {
  // [링크 텍스트](URL) 형식을 [<a href="URL" target="_blank" rel="noopener noreferrer">링크 텍스트</a>]로 변환
  return text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '[<a href="$2" target="_blank" rel="noopener noreferrer" style="color: #3b82f6; text-decoration: underline; font-weight: 600;">$1</a>]');
};

// React 컴포넌트용 설명 번역 함수 (dangerouslySetInnerHTML 사용)
export const translateDescription = (description: string): string => {
  if (!description) return '';
  
  // 기본적인 번역 매핑
  const translations: { [key: string]: string } = {
    'Remove dead rules from stylesheets and defer the loading of CSS not used for above-the-fold content to reduce unnecessary bytes consumed by network activity.':
      '스타일시트에서 사용되지 않는 규칙을 제거하고, 스크롤 없이 볼 수 있는 영역에서 사용되지 않는 CSS의 로딩을 지연시켜 불필요한 네트워크 사용량을 줄이세요.',
    
    'Remove unused JavaScript to reduce bytes consumed by network activity.':
      '사용되지 않는 JavaScript를 제거하여 네트워크 활동으로 소비되는 바이트를 줄이세요.',
    
    'Reduce unused JavaScript and defer loading scripts until they are required to decrease bytes consumed by network activity.':
      '사용되지 않는 JavaScript를 줄이고 필요할 때까지 스크립트 로딩을 지연시켜 네트워크 활동으로 소비되는 바이트를 줄이세요.',
    
    'Reduce unused JavaScript and defer loading scripts until they are required to decrease bytes consumed by network activity. [Learn how to reduce unused JavaScript](https://developer.chrome.com/docs/lighthouse/performance/unused-javascript/).':
      '사용되지 않는 JavaScript를 줄이고 필요할 때까지 스크립트 로딩을 지연시켜 네트워크 활동으로 소비되는 바이트를 줄이세요. [미사용 JavaScript 줄이는 방법 알아보기](https://developer.chrome.com/docs/lighthouse/performance/unused-javascript/).',
    
    'Resources are blocking the first paint of your page. Consider delivering critical JS/CSS inline and deferring all non-critical JS/styles.':
      '리소스가 페이지의 첫 번째 페인트를 차단하고 있습니다. 중요한 JS/CSS를 인라인으로 제공하고 중요하지 않은 모든 JS/스타일을 지연시키는 것을 고려하세요.',
    
    'Minifying CSS files can reduce network payload sizes.':
      'CSS 파일을 압축하면 네트워크 페이로드 크기를 줄일 수 있습니다.',
    
    'Minifying JavaScript files can reduce payload sizes and script parse time.':
      'JavaScript 파일을 압축하면 페이로드 크기와 스크립트 파싱 시간을 줄일 수 있습니다.',
    
    'Image formats like WebP and AVIF often provide better compression than PNG or JPEG, which means faster downloads and less data consumption.':
      'WebP 및 AVIF와 같은 이미지 포맷은 PNG나 JPEG보다 더 나은 압축을 제공하여 더 빠른 다운로드와 적은 데이터 사용을 가능하게 합니다.',
    
    'Image formats like WebP and AVIF often provide better compression than PNG or JPEG, which means faster downloads and less data consumption. [Learn more about modern image formats](https://developer.chrome.com/docs/lighthouse/performance/uses-webp-images/).':
      'WebP 및 AVIF와 같은 이미지 포맷은 PNG나 JPEG보다 더 나은 압축을 제공하여 더 빠른 다운로드와 적은 데이터 사용을 가능하게 합니다. [최신 이미지 포맷에 대해 자세히 알아보기](https://developer.chrome.com/docs/lighthouse/performance/uses-webp-images/).',
    
    'Consider lazy-loading offscreen and hidden images after all critical resources have finished loading to lower time to interactive.':
      '중요한 리소스 로딩이 모두 완료된 후 화면 밖과 숨겨진 이미지를 지연 로딩하여 상호작용 시간을 줄이는 것을 고려하세요.',
    
    'Consider lazy-loading offscreen and hidden images after all critical resources have finished loading to lower time to interactive. [Learn how to defer offscreen images](https://developer.chrome.com/docs/lighthouse/performance/offscreen-images/).':
      '중요한 리소스 로딩이 모두 완료된 후 화면 밖과 숨겨진 이미지를 지연 로딩하여 상호작용 시간을 줄이는 것을 고려하세요. [화면 밖 이미지 지연 로딩하는 방법 알아보기](https://developer.chrome.com/docs/lighthouse/performance/offscreen-images/).',
    
    'Serve images that are appropriately-sized to save cellular data and improve load time.':
      '셀룰러 데이터를 절약하고 로딩 시간을 개선하기 위해 적절한 크기의 이미지를 제공하세요.',
    
    'Serve images that are appropriately-sized to save cellular data and improve load time. [Learn how to size images](https://developer.chrome.com/docs/lighthouse/performance/uses-responsive-images/).':
      '셀룰러 데이터를 절약하고 로딩 시간을 개선하기 위해 적절한 크기의 이미지를 제공하세요. [이미지 크기 조정 방법 알아보기](https://developer.chrome.com/docs/lighthouse/performance/uses-responsive-images/).',
    
    'Resources are blocking the first paint of your page. Consider delivering critical JS/CSS inline and deferring all non-critical JS/styles. [Learn how to eliminate render-blocking resources](https://developer.chrome.com/docs/lighthouse/performance/render-blocking-resources/).':
      '리소스가 페이지의 첫 번째 페인트를 차단하고 있습니다. 중요한 JS/CSS를 인라인으로 제공하고 중요하지 않은 모든 JS/스타일을 지연시키는 것을 고려하세요. [렌더링 차단 리소스 제거하는 방법 알아보기](https://developer.chrome.com/docs/lighthouse/performance/render-blocking-resources/).',
    
    'Minifying JavaScript files can reduce payload sizes and script parse time. [Learn how to minify JavaScript](https://developer.chrome.com/docs/lighthouse/performance/unminified-javascript/).':
      'JavaScript 파일을 압축하면 페이로드 크기와 스크립트 파싱 시간을 줄일 수 있습니다. [JavaScript 압축하는 방법 알아보기](https://developer.chrome.com/docs/lighthouse/performance/unminified-javascript/).',
    
    'Redirects introduce additional delays before the page can be loaded. [Learn how to avoid page redirects](https://developer.chrome.com/docs/lighthouse/performance/redirects/).':
      '리다이렉트는 페이지가 로드되기 전에 추가 지연을 발생시킵니다. [페이지 리다이렉트 방지 방법 알아보기](https://developer.chrome.com/docs/lighthouse/performance/redirects/).',
    
    'Reduce unused rules from stylesheets and defer CSS not used for above-the-fold content to decrease bytes consumed by network activity. [Learn how to reduce unused CSS](https://developer.chrome.com/docs/lighthouse/performance/unused-css-rules/).':
      '스타일시트에서 사용되지 않는 규칙을 제거하고 스크롤 없이 볼 수 있는 영역에서 사용되지 않는 CSS의 로딩을 지연시켜 네트워크 활동으로 소비되는 바이트를 줄이세요. [미사용 CSS 줄이는 방법 알아보기](https://developer.chrome.com/docs/lighthouse/performance/unused-css-rules/).',
    
    'Optimized images load faster and consume less cellular data. [Learn how to efficiently encode images](https://developer.chrome.com/docs/lighthouse/performance/uses-optimized-images/).':
      '최적화된 이미지는 더 빠르게 로드되고 모바일 데이터를 적게 소비합니다. [이미지를 효율적으로 인코딩하는 방법 알아보기](https://developer.chrome.com/docs/lighthouse/performance/uses-optimized-images/).',
    
    'Efficiently encode images':
      '이미지를 효율적으로 인코딩하세요'
  };
  
  const translatedText = translations[description] || description;
  return convertMarkdownLinksToHtml(translatedText);
};

// 이미지 base64 데이터 (실제 이미지를 base64로 인코딩한 값들)
const getImageDataUrl = (imageName: string): string => {
  // 실제로는 이미지 파일을 base64로 인코딩해야 하지만,
  // 여기서는 이모지 폴백을 사용합니다
  const imageMap: { [key: string]: string } = {
    'javascript': '⚡',
    'http': '🌐',
    'font': '🔤',
    'resource': '📦',
    'page': '📄'
  };
  return imageMap[imageName] || '🔧';
};

// 주제에 맞는 이미지 정보 반환 (HTML 보고서용)
const getImageForTopic = (title: string) => {
  const titleLower = title.toLowerCase();
  
  if (titleLower.includes('javascript') || titleLower.includes('스크립트') || 
      titleLower.includes('js') || titleLower.includes('blocking')) {
    return { emoji: '⚡', alt: 'JavaScript 최적화' };
  } else if (titleLower.includes('http') || titleLower.includes('요청') || 
             titleLower.includes('request')) {
    return { emoji: '🌐', alt: 'HTTP 요청 최적화' };
  } else if (titleLower.includes('폰트') || titleLower.includes('font')) {
    return { emoji: '🔤', alt: '외부 폰트 사용' };
  } else if (titleLower.includes('리소스') || titleLower.includes('resource') ||
             titleLower.includes('css') || titleLower.includes('파일')) {
    return { emoji: '📦', alt: '리소스 관리' };
  } else if (titleLower.includes('페이지') || titleLower.includes('용량') ||
             titleLower.includes('크기') || titleLower.includes('excessive')) {
    return { emoji: '📄', alt: '페이지 용량 최적화' };
  }
  
  return { emoji: '🔧', alt: '기타 개선사항' };
};

// LLM 분석을 HTML로 변환하는 함수
const generateLLMAnalysisHTML = (analysis: string): string => {
  if (!analysis) return '';

  const lines = analysis.split('\n');
  let html = '';
  let inImprovementSection = false;
  let currentSection: any = null;

  for (const line of lines) {
    const trimmedLine = line.trim();
    if (trimmedLine.length === 0) continue;

    // 메인 섹션 감지 (### 1. ✅ **우수한 점** 또는 ### 2. ⚠️ **개선이 필요한 영역**)
    if (trimmedLine.match(/^#{1,3}\s+\d+\.\s*[✅⚠️]?\s*\*\*(.+?)\*\*/)) {
      const isImprovementSection = trimmedLine.includes('개선') || trimmedLine.includes('문제') || trimmedLine.includes('권장');
      
      if (isImprovementSection) {
        inImprovementSection = true;
        // 개선 영역 섹션 시작 - 서브섹션들을 파싱
        continue;
      } else {
        // 우수한점 섹션
        inImprovementSection = false;
        html += `<div style="margin-bottom: 25px; padding: 25px; background-color: #ffffff; border-radius: 16px; border: 1px solid rgba(59, 130, 246, 0.1); box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);">`;
        const match = trimmedLine.match(/\*\*(.+?)\*\*/);
        const title = match ? match[1] : '우수한 점';
        html += `<h4 style="margin: 0 0 15px 0; color: #1e40af; font-size: 24px; font-weight: 700; border-bottom: 2px solid #3b82f6; padding-bottom: 8px;">${title}</h4>`;
        html += `<div style="font-size: 18px; line-height: 1.7; color: #374151;">`;
        continue;
      }
    }

    // 개선 영역에서 **주제:** 패턴 감지
    if (inImprovementSection && trimmedLine.match(/^\*\*([^*:]+):\*\*$/)) {
      // 이전 섹션 닫기
      if (currentSection) {
        html += `</div></div></div>`;
      }
      
      // 새 개선 서브섹션 시작
      const title = trimmedLine.replace(/^\*\*([^*:]+):\*\*$/, '$1');
      const imageInfo = getImageForTopic(title);
      
      html += `<div style="display: flex; gap: 20px; margin-bottom: 20px; padding: 20px; background-color: #f8fafc; border-radius: 12px; border: 1px solid rgba(148, 163, 184, 0.2); border-left: 4px solid #3b82f6;">`;
      
      // 이미지 영역 (이모지 사용)
      html += `<div style="flex-shrink: 0;">`;
      html += `<div style="width: 300px; height: 220px; border-radius: 16px; border: 3px solid rgba(59, 130, 246, 0.2); box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15); display: flex; align-items: center; justify-content: center; background-color: #f1f5f9; font-size: 64px;">${imageInfo.emoji}</div>`;
      html += `</div>`;
      
      // 텍스트 콘텐츠 영역
      html += `<div style="flex: 1;">`;
      html += `<h5 style="margin: 0 0 18px 0; color: #1e40af; font-size: 24px; font-weight: 700; display: flex; align-items: center; gap: 10px;">🔧 ${title}</h5>`;
      html += `<div style="font-size: 18px; line-height: 1.7; color: #475569;">`;
      
      currentSection = { title, content: [] };
      continue;
    }

    // 일반 내용 처리
    if (inImprovementSection && currentSection) {
      // 개선 섹션의 내용
      if (trimmedLine.startsWith('-') || trimmedLine.startsWith('•')) {
        html += `<div style="padding-left: 16px; position: relative; margin-bottom: 6px;">`;
        html += `<span style="position: absolute; left: 0; color: #3b82f6; font-weight: 600;">•</span>`;
        html += `${trimmedLine.substring(1).trim()}`;
        html += `</div>`;
      } else if (trimmedLine.includes('**')) {
        const formatted = trimmedLine.replace(/\*\*(.*?)\*\*/g, '<strong style="color: #1e40af; font-weight: 600;">$1</strong>');
        html += `<div style="margin-bottom: 8px;">${formatted}</div>`;
      } else {
        html += `<div style="margin-bottom: 8px;">${trimmedLine}</div>`;
      }
    } else if (!inImprovementSection) {
      // 우수한점 섹션의 내용
      if (trimmedLine.startsWith('-') || trimmedLine.startsWith('•')) {
        html += `<div style="padding-left: 16px; position: relative; margin-bottom: 6px;">`;
        html += `<span style="position: absolute; left: 0; color: #10b981; font-weight: 600;">✓</span>`;
        html += `${trimmedLine.substring(1).trim()}`;
        html += `</div>`;
      } else if (trimmedLine.includes('**')) {
        const formatted = trimmedLine.replace(/\*\*(.*?)\*\*/g, '<strong style="color: #1e40af; font-weight: 700;">$1</strong>');
        html += `<div style="margin-bottom: 8px;">${formatted}</div>`;
      } else {
        html += `<div style="margin-bottom: 8px;">${trimmedLine}</div>`;
      }
    }
  }

  // 열린 태그들 닫기
  if (currentSection) {
    html += `</div></div></div>`;
  } else if (!inImprovementSection) {
    html += `</div></div>`;
  }

  return html;
};

/**
 * PageSpeedResult를 SiteData로 변환하는 함수
 */
const convertToSiteData = (pageSpeedData: PageSpeedResult): SiteData => {
  return {
    fcp_desktop: pageSpeedData.desktop.firstContentfulPaint,
    fcp_mobile: pageSpeedData.mobile.firstContentfulPaint,
    lcp_desktop: pageSpeedData.desktop.largestContentfulPaint,
    lcp_mobile: pageSpeedData.mobile.largestContentfulPaint,
    tbt_desktop: pageSpeedData.desktop.totalBlockingTime,
    tbt_mobile: pageSpeedData.mobile.totalBlockingTime,
    total_size_desktop: pageSpeedData.desktop.pageSize?.totalSize,
    total_size_mobile: pageSpeedData.mobile.pageSize?.totalSize,
    url: pageSpeedData.url
  };
};

// 마크다운 보고서 생성 함수
export const generateMarkdownReport = (
  pageSpeedData: PageSpeedResult,
  url: string,
  llmAnalysis?: string | null,
  hallOfFameData?: SiteData[],
  customRatings?: { desktop: number; mobile: number; overall: number }
): string => {
  if (!pageSpeedData) return '';

  let markdown = '';
  
  // 제목과 기본 정보
  markdown += `# 웹사이트 성능 분석 보고서\n\n`;
  markdown += `**분석 URL:** ${url}\n\n`;
  markdown += `**분석 완료 시간:** ${new Date(pageSpeedData.timestamp).toLocaleString('ko-KR')}\n\n`;
  markdown += `---\n\n`;

  // 페이지 용량 분석
  markdown += `## 📦 페이지 용량 분석\n\n`;
  
  // 데스크톱 용량 정보
  markdown += `### 🖥️ 데스크톱\n\n`;
  const desktopTotalSize = pageSpeedData.desktop.pageSize?.totalSize || 0;
  const desktopResourceCount = pageSpeedData.desktop.pageSize?.resourceCount || 0;
  const desktopBreakdown = pageSpeedData.desktop.pageSize?.resourceBreakdown || {};
  const desktopJSSize = desktopBreakdown['script'] || desktopBreakdown['Script'] || desktopBreakdown['javascript'] || desktopBreakdown['JavaScript'] || 0;
  const desktopImageSize = desktopBreakdown['image'] || desktopBreakdown['Image'] || desktopBreakdown['images'] || desktopBreakdown['Images'] || 0;
  const desktopCSSSize = desktopBreakdown['stylesheet'] || desktopBreakdown['Stylesheet'] || desktopBreakdown['css'] || desktopBreakdown['CSS'] || 0;
  
  markdown += `- **총 페이지 크기:** ${desktopTotalSize > 1024 * 1024 ? `${(desktopTotalSize / 1024 / 1024).toFixed(2)} MB` : `${(desktopTotalSize / 1024).toFixed(0)} KB`}\n`;
  markdown += `- **리소스 개수:** ${desktopResourceCount}개\n`;
  
  markdown += `- **JavaScript:** ${desktopJSSize > 1024 * 1024 ? `${(desktopJSSize / 1024 / 1024).toFixed(2)} MB` : `${(desktopJSSize / 1024).toFixed(0)} KB`}\n`;
  markdown += `- **이미지:** ${desktopImageSize > 1024 * 1024 ? `${(desktopImageSize / 1024 / 1024).toFixed(2)} MB` : `${(desktopImageSize / 1024).toFixed(0)} KB`}\n`;
  markdown += `- **CSS:** ${desktopCSSSize > 1024 * 1024 ? `${(desktopCSSSize / 1024 / 1024).toFixed(2)} MB` : `${(desktopCSSSize / 1024).toFixed(0)} KB`}\n\n`;
  
  // 모바일 용량 정보
  markdown += `### 📱 모바일\n\n`;
  const mobileTotalSize = pageSpeedData.mobile.pageSize?.totalSize || 0;
  const mobileResourceCount = pageSpeedData.mobile.pageSize?.resourceCount || 0;
  const mobileBreakdown = pageSpeedData.mobile.pageSize?.resourceBreakdown || {};
  const mobileJSSize = mobileBreakdown['script'] || mobileBreakdown['Script'] || mobileBreakdown['javascript'] || mobileBreakdown['JavaScript'] || 0;
  const mobileImageSize = mobileBreakdown['image'] || mobileBreakdown['Image'] || mobileBreakdown['images'] || mobileBreakdown['Images'] || 0;
  const mobileCSSSize = mobileBreakdown['stylesheet'] || mobileBreakdown['Stylesheet'] || mobileBreakdown['css'] || mobileBreakdown['CSS'] || 0;
  
  markdown += `- **총 페이지 크기:** ${mobileTotalSize > 1024 * 1024 ? `${(mobileTotalSize / 1024 / 1024).toFixed(2)} MB` : `${(mobileTotalSize / 1024).toFixed(0)} KB`}\n`;
  markdown += `- **리소스 개수:** ${mobileResourceCount}개\n`;
  markdown += `- **JavaScript:** ${mobileJSSize > 1024 * 1024 ? `${(mobileJSSize / 1024 / 1024).toFixed(2)} MB` : `${(mobileJSSize / 1024).toFixed(0)} KB`}\n`;
  markdown += `- **이미지:** ${mobileImageSize > 1024 * 1024 ? `${(mobileImageSize / 1024 / 1024).toFixed(2)} MB` : `${(mobileImageSize / 1024).toFixed(0)} KB`}\n`;
  markdown += `- **CSS:** ${mobileCSSSize > 1024 * 1024 ? `${(mobileCSSSize / 1024 / 1024).toFixed(2)} MB` : `${(mobileCSSSize / 1024).toFixed(0)} KB`}\n\n`;

  // 성능 점수 계산 - customRatings 우선, 없으면 계산
  let desktopScore = 0;
  let mobileScore = 0;
  let desktopGrade = '-';
  let mobileGrade = '-';

  if (customRatings) {
    // 명예의전당 보고서에서 전달받은 커스텀 점수 사용
    desktopScore = customRatings.desktop;
    mobileScore = customRatings.mobile;
    desktopGrade = getRatingGrade(desktopScore);
    mobileGrade = getRatingGrade(mobileScore);
  } else if (hallOfFameData && hallOfFameData.length > 0) {
    // Hall of Fame 데이터로 계산
    const currentSite = convertToSiteData(pageSpeedData);
    desktopScore = calculateDesktopRating(currentSite, hallOfFameData);
    mobileScore = calculateMobileRating(currentSite, hallOfFameData);
    desktopGrade = getRatingGrade(desktopScore);
    mobileGrade = getRatingGrade(mobileScore);
  }

  // 성능 점수
  markdown += `## 🏆 성능 점수\n\n`;
  if (desktopScore > 0 || mobileScore > 0) {
    markdown += `### 🖥️ 데스크톱: ${desktopScore}점 (${desktopGrade} 등급)\n\n`;
    markdown += `### 📱 모바일: ${mobileScore}점 (${mobileGrade} 등급)\n\n`;
  } else {
    markdown += `### 🖥️ 데스크톱: ⏳ 준비중\n`;
    markdown += `명예의전당 데이터를 불러올 수 없습니다.\n\n`;
    markdown += `### 📱 모바일: ⏳ 준비중\n`;
    markdown += `명예의전당 데이터를 불러올 수 없습니다.\n\n`;
  }

  // 분석총평
  markdown += `## 📝 분석총평\n\n`;
  markdown += `**[참고]** 웹사이트 성능 최적화 권장 기준: 페이지 용량 35MB 이하(PC 기준), HTTP 요청 수 50~60회 이하를 권장합니다. 이 기준을 과도하게 초과할 경우 사용자 경험에 영향을 미쳐 이탈률 증가로 이어질 수 있습니다.\n\n`;
  if (llmAnalysis) {
    markdown += `${llmAnalysis}\n\n`;
  } else {
    markdown += `현재 웹사이트의 성능 분석 결과를 종합해보면 다음과 같습니다.\n\n`;
    markdown += `현재 자체 성능 점수 계산식을 개발 중이며, 더 정확한 성능 평가를 제공할 예정입니다.\n\n`;
    markdown += `### 주요 성능 지표 분석:\n`;
    markdown += `- 첫 번째 콘텐츠 표시 시간(FCP): 데스크톱 ${safeToFixed(pageSpeedData.desktop.firstContentfulPaint, 1)}초, 모바일 ${safeToFixed(pageSpeedData.mobile.firstContentfulPaint, 1)}초\n`;
    markdown += `- 최대 콘텐츠 표시 시간(LCP): 데스크톱 ${safeToFixed(pageSpeedData.desktop.largestContentfulPaint, 1)}초, 모바일 ${safeToFixed(pageSpeedData.mobile.largestContentfulPaint, 1)}초\n`;
    markdown += `- 누적 레이아웃 이동(CLS): 데스크톱 ${safeToFixed(pageSpeedData.desktop.cumulativeLayoutShift, 3)}, 모바일 ${safeToFixed(pageSpeedData.mobile.cumulativeLayoutShift, 3)}\n\n`;
    markdown += `### 개선 권장사항:\n`;
    markdown += `1. JavaScript 파일 압축 및 사용하지 않는 코드 제거\n`;
    markdown += `2. 이미지 최적화 및 차세대 이미지 형식 활용\n`;
    markdown += `3. CSS 파일 압축 및 중요하지 않은 CSS 지연 로딩\n`;
    markdown += `4. 서버 응답 시간 단축 및 캐시 정책 개선\n`;
    markdown += `5. 렌더링 차단 리소스 최적화\n\n`;
  }

  // Core Web Vitals
  markdown += `## ⚡ 웹사이트 성능 지표 (Core Web Vitals)\n\n`;
  markdown += `Core Web Vitals는 Google이 제시하는 웹사이트의 핵심 성능 지표입니다. 실제 사용자가 경험하는 페이지 로딩 속도, 상호작용 응답성, 시각적 안정성을 측정합니다.\n\n`;

  // 데스크톱 Core Web Vitals
  markdown += `### 🖥️ 데스크톱 성능\n\n`;
  markdown += `#### 🎨 첫 번째 콘텐츠 표시 시간 (FCP): ${safeToFixed(pageSpeedData.desktop.firstContentfulPaint, 1)}초\n`;
  markdown += `**평가:** ${(pageSpeedData.desktop.firstContentfulPaint || 0) <= 1.8 ? '좋음' : (pageSpeedData.desktop.firstContentfulPaint || 0) <= 3.0 ? '보통' : '개선 필요'}\n\n`;
  markdown += `페이지가 로드되기 시작한 시점부터 페이지 콘텐츠의 일부가 화면에 처음 렌더링되는 시점까지의 시간입니다.\n`;
  markdown += `데스크톱 권장: 1.8초 이하 (우수), 3.0초 이하 (보통)\n\n`;

  if (pageSpeedData.desktop.totalBlockingTime !== undefined) {
    markdown += `#### 🚫 총 차단 시간 (TBT): ${safeToFixed(pageSpeedData.desktop.totalBlockingTime, 0)}ms\n`;
    markdown += `**평가:** ${(pageSpeedData.desktop.totalBlockingTime || 0) <= 200 ? '좋음' : (pageSpeedData.desktop.totalBlockingTime || 0) <= 600 ? '보통' : '개선 필요'}\n\n`;
    markdown += `FCP와 TTI 사이에 메인 스레드가 차단되어 입력 응답을 방해하는 총 시간입니다.\n`;
    markdown += `데스크톱 권장: 200ms 이하 (우수), 600ms 이하 (보통)\n\n`;
  }

  // 모바일 Core Web Vitals
  markdown += `### 📱 모바일 성능\n\n`;
  markdown += `#### 🎨 첫 번째 콘텐츠 표시 시간 (FCP): ${safeToFixed(pageSpeedData.mobile.firstContentfulPaint, 1)}초\n`;
  markdown += `**평가:** ${(pageSpeedData.mobile.firstContentfulPaint || 0) <= 2.0 ? '좋음' : (pageSpeedData.mobile.firstContentfulPaint || 0) <= 4.0 ? '보통' : '개선 필요'}\n\n`;
  markdown += `페이지가 로드되기 시작한 시점부터 페이지 콘텐츠의 일부가 화면에 처음 렌더링되는 시점까지의 시간입니다.\n`;
  markdown += `모바일 권장: 2.0초 이하 (우수), 4.0초 이하 (보통)\n\n`;

  if (pageSpeedData.mobile.totalBlockingTime !== undefined) {
    markdown += `#### 🚫 총 차단 시간 (TBT): ${safeToFixed(pageSpeedData.mobile.totalBlockingTime, 0)}ms\n`;
    markdown += `**평가:** ${(pageSpeedData.mobile.totalBlockingTime || 0) <= 300 ? '좋음' : (pageSpeedData.mobile.totalBlockingTime || 0) <= 900 ? '보통' : '개선 필요'}\n\n`;
    markdown += `FCP와 TTI 사이에 메인 스레드가 차단되어 입력 응답을 방해하는 총 시간입니다.\n`;
    markdown += `모바일 권장: 300ms 이하 (우수), 900ms 이하 (보통)\n\n`;
  }

  // 데스크톱 핵심 성능 개선 기회
  if (pageSpeedData.desktop.opportunities && pageSpeedData.desktop.opportunities.length > 0) {
    const desktopOpportunities = getImportantOpportunities(pageSpeedData.desktop.opportunities);
    if (desktopOpportunities.length > 0) {
      markdown += `## 💡 핵심 성능 개선 기회 (🖥️ 데스크톱)\n\n`;
      desktopOpportunities.slice(0, 5).forEach((opportunity, index) => {
        markdown += `### ${index + 1}. ${translateOpportunityTitle(opportunity.title)}\n\n`;
        markdown += `${translateDescription(opportunity.description)}\n\n`;
        
        // 리소스 상세 정보 추가
        if (opportunity.details && opportunity.details.items && opportunity.details.items.length > 0) {
          const excludePatterns = ['optimizer.php'];
          const filteredItems = opportunity.details.items.filter((item: any) => {
            const url = item.url || item.source || item.node?.snippet || '';
            return !excludePatterns.some(pattern => url.includes(pattern));
          });
          
          if (filteredItems.length > 0) {
            markdown += `**관련 리소스:**\n`;
            filteredItems.slice(0, 3).forEach((item: any) => {
              const url = item.url || item.source || item.node?.snippet || '알 수 없음';
              const cleanUrl = url.replace(/^https?:\/\//, '');
              markdown += `- ${cleanUrl}\n`;
            });
            if (filteredItems.length > 3) {
              markdown += `- ... 외 ${filteredItems.length - 3}개 항목\n`;
            }
            markdown += `\n`;
          }
        }
      });
    }
  }

  // 모바일 핵심 성능 개선 기회
  if (pageSpeedData.mobile.opportunities && pageSpeedData.mobile.opportunities.length > 0) {
    const mobileOpportunities = getImportantOpportunities(pageSpeedData.mobile.opportunities);
    if (mobileOpportunities.length > 0) {
      markdown += `## 💡 핵심 성능 개선 기회 (📱 모바일)\n\n`;
      mobileOpportunities.slice(0, 5).forEach((opportunity, index) => {
        markdown += `### ${index + 1}. ${translateOpportunityTitle(opportunity.title)}\n\n`;
        markdown += `${translateDescription(opportunity.description)}\n\n`;
        
        // 리소스 상세 정보 추가
        if (opportunity.details && opportunity.details.items && opportunity.details.items.length > 0) {
          const excludePatterns = ['optimizer.php'];
          const filteredItems = opportunity.details.items.filter((item: any) => {
            const url = item.url || item.source || item.node?.snippet || '';
            return !excludePatterns.some(pattern => url.includes(pattern));
          });
          
          if (filteredItems.length > 0) {
            markdown += `**관련 리소스:**\n`;
            filteredItems.slice(0, 3).forEach((item: any) => {
              const url = item.url || item.source || item.node?.snippet || '알 수 없음';
              const cleanUrl = url.replace(/^https?:\/\//, '');
              markdown += `- ${cleanUrl}\n`;
            });
            if (filteredItems.length > 3) {
              markdown += `- ... 외 ${filteredItems.length - 3}개 항목\n`;
            }
            markdown += `\n`;
          }
        }
      });
    }
  }

  // 마무리
  markdown += `---\n\n`;
  markdown += `*이 보고서는 ${new Date().toLocaleString('ko-KR')}에 생성되었습니다.*\n`;
  
  return markdown;
};

// HTML 보고서 생성 함수 (매우 길어서 별도 처리)
export const generateHtmlReport = (
  pageSpeedData: PageSpeedResult,
  url: string,
  llmAnalysis?: string | null,
  hallOfFameData?: SiteData[],
  customRatings?: { desktop: number; mobile: number; overall: number }
): string => {
  if (!pageSpeedData) return '';

  // 용량을 포맷하는 헬퍼 함수
  const formatBytes = (bytes: number) => {
    return bytes > 1024 * 1024
      ? `${(bytes / 1024 / 1024).toFixed(2)} MB`
      : `${(bytes / 1024).toFixed(0)} KB`;
  };

  // 성능 점수 계산 - customRatings 우선, 없으면 계산
  let desktopScore = 0;
  let mobileScore = 0;
  let desktopGrade = '-';
  let mobileGrade = '-';
  let desktopColor = '#6b7280';
  let mobileColor = '#6b7280';

  if (customRatings) {
    // 명예의전당 보고서에서 전달받은 커스텀 점수 사용
    desktopScore = customRatings.desktop;
    mobileScore = customRatings.mobile;
    desktopGrade = getRatingGrade(desktopScore);
    mobileGrade = getRatingGrade(mobileScore);
    desktopColor = getRatingColor(desktopScore);
    mobileColor = getRatingColor(mobileScore);
  } else if (hallOfFameData && hallOfFameData.length > 0) {
    // Hall of Fame 데이터로 계산
    const currentSite = convertToSiteData(pageSpeedData);
    desktopScore = calculateDesktopRating(currentSite, hallOfFameData);
    mobileScore = calculateMobileRating(currentSite, hallOfFameData);
    desktopGrade = getRatingGrade(desktopScore);
    mobileGrade = getRatingGrade(mobileScore);
    desktopColor = getRatingColor(desktopScore);
    mobileColor = getRatingColor(mobileScore);
  }

  let html = `
<!DOCTYPE html>
<html lang="ko">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>웹사이트 성능 분석 보고서</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            background-color: #f8fafc;
            color: #333;
            line-height: 1.6;
        }
        .main-container {
            padding: 0 30px;
            max-width: 1200px;
            margin: 0 auto;
        }
        .section-container {
            background: white;
            border-radius: 20px;
            padding: 25px;
            margin-bottom: 30px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
            border: 1px solid rgba(59, 130, 246, 0.1);
        }
        .section-title {
            margin-bottom: 20px;
            color: #1e40af;
            font-size: 20px;
            font-weight: 600;
            display: flex;
            align-items: center;
            gap: 8px;
        }
        .summary-info {
            margin-bottom: 25px;
            padding: 20px;
            background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
            border-radius: 15px;
            border: 1px solid rgba(59, 130, 246, 0.2);
        }
        .summary-text {
            margin: 0;
            color: #1e40af;
            font-weight: 600;
            font-size: 15px;
        }
        .url-link {
            font-family: 'Consolas', Monaco, 'Courier New', monospace;
            word-break: break-all;
            color: #1e40af;
            text-decoration: underline;
            cursor: pointer;
        }
        .performance-scores {
            margin-bottom: 25px;
        }
        .performance-title {
            margin-bottom: 20px;
            color: #1e40af;
            font-size: 18px;
            font-weight: 600;
        }
        .scores-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
        }
        .score-card {
            padding: 25px;
            border-radius: 15px;
            text-align: center;
            border: 2px solid;
            box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
        }
        .desktop-card {
            background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
            border-color: #0ea5e9;
        }
        .mobile-card {
            background: linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%);
            border-color: #ec4899;
        }
        .device-label {
            font-size: 14px;
            margin-bottom: 10px;
            font-weight: 600;
        }
        .desktop-label {
            color: #0369a1;
        }
        .mobile-label {
            color: #be185d;
        }
        .score-number {
            font-size: 48px;
            font-weight: bold;
            margin-bottom: 5px;
        }
        .score-label {
            font-size: 12px;
            font-weight: 500;
        }
        .score-badge {
            margin-top: 10px;
            padding: 6px 14px;
            border-radius: 20px;
            font-size: 12px;
            font-weight: bold;
            display: inline-block;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }
        .page-size-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 25px;
            margin-bottom: 30px;
        }
        .device-section {
            padding: 25px;
            border-radius: 15px;
            border: 2px solid;
        }
        .desktop-section {
            background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
            border-color: #0ea5e9;
        }
        .mobile-section {
            background: linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%);
            border-color: #ec4899;
        }
        .size-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 8px 0;
            border-bottom: 1px solid rgba(0, 0, 0, 0.1);
        }
        .size-item:last-child {
            border-bottom: none;
        }
        .size-label {
            font-weight: 500;
            color: #4a5568;
        }
        .size-value {
            font-weight: 600;
            color: #2d3748;
        }
        .analysis-summary {
            background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
            border-radius: 24px;
            padding: 30px;
            margin-bottom: 35px;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
            border: 2px solid #e2e8f0;
        }
        .analysis-title {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 25px;
            color: #1e40af;
            font-size: 22px;
            font-weight: 700;
        }
        .analysis-content {
            color: #4a5568;
            line-height: 1.8;
            font-size: 16px;
        }
        .web-vitals {
            margin-bottom: 30px;
        }
        .vitals-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 25px;
        }
        .vitals-section {
            padding: 25px;
            border-radius: 15px;
            border: 2px solid;
        }
        .vitals-title {
            font-size: 16px;
            font-weight: 600;
            margin-bottom: 20px;
            display: flex;
            align-items: center;
            gap: 8px;
        }
        .metric-item {
            margin-bottom: 15px;
            padding: 15px;
            background: rgba(255, 255, 255, 0.7);
            border-radius: 10px;
            border: 1px solid rgba(0, 0, 0, 0.1);
        }
        .metric-label {
            font-weight: 500;
            color: #4a5568;
            font-size: 14px;
        }
        .metric-value {
            font-size: 18px;
            font-weight: 700;
            margin: 5px 0;
        }
        .metric-status {
            font-size: 12px;
            font-weight: 600;
            padding: 3px 8px;
            border-radius: 12px;
            display: inline-block;
        }
        .opportunities {
            margin-bottom: 30px;
        }
        .opportunity-item {
            margin-bottom: 25px;
            padding: 20px;
            background: white;
            border-radius: 12px;
            border: 1px solid #e2e8f0;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        }
        .opportunity-title {
            font-size: 16px;
            font-weight: 600;
            color: #2d3748;
            margin-bottom: 8px;
        }
        .opportunity-description {
            color: #4a5568;
            margin-bottom: 15px;
            line-height: 1.6;
        }
        .resources-list {
            margin-top: 10px;
        }
        .resource-item {
            display: grid;
            grid-template-columns: 1fr;
            gap: 15px;
            font-size: 11px;
            color: #718096;
            padding: 6px 8px;
            border-bottom: 1px solid #f7fafc;
        }
        .resource-link {
            word-break: break-all;
            line-height: 1.4;
            font-size: 12px;
            max-width: 900px;
            font-family: Consolas, Monaco, 'Courier New', monospace;
            background-color: #f8f9fa;
            padding: 6px 10px;
            border-radius: 8px;
            border: 1px solid #e9ecef;
            color: #1e40af;
            font-weight: 600;
            letter-spacing: 0.3px;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            text-decoration: none;
            display: block;
        }
        .resource-link:hover {
            background-color: #e3f2fd;
            color: #3b82f6;
        }
    </style>
</head>
<body>
    <div class="main-container">
        <div class="section-container">
            <h1 class="section-title">🏆 웹사이트 성능 분석 보고서</h1>
            
            <div class="summary-info">
                <div class="summary-text">
                    <div>
                        <strong>📊 분석 URL:</strong> 
                        <a href="${pageSpeedData.url}" target="_blank" class="url-link" title="클릭하여 ${pageSpeedData.url} 방문">
                            ${pageSpeedData.url}
                        </a>
                    </div>
                    <div>
                        <strong>⏰ 분석 완료 시간:</strong> ${new Date(pageSpeedData.timestamp).toLocaleString('ko-KR')}
                    </div>
                </div>
            </div>

            <!-- 성능 점수 -->
            <div class="performance-scores">
                <h4 class="performance-title">🏆 성능 점수</h4>
                ${(desktopScore > 0 || mobileScore > 0) ? '' : `
                <p style="color: #ef4444; font-size: 13px; margin-bottom: 15px;">⚠️ 성능 점수를 계산할 수 없습니다.</p>
                `}
                <div class="scores-grid">
                    <!-- Desktop 점수 -->
                    <div class="score-card desktop-card">
                        <div class="device-label desktop-label">🖥️ 데스크톱</div>
                        <div class="score-number" style="color: ${desktopScore > 0 ? desktopColor : '#6b7280'};">
                            ${desktopScore > 0 ? desktopScore : '준비중'}
                        </div>
                        <div class="score-label desktop-label">성능 점수</div>
                        <div class="score-badge" style="
                            background-color: ${desktopScore > 0 ? desktopColor + '20' : '#f3f4f6'};
                            color: ${desktopScore > 0 ? desktopColor : '#6b7280'};
                        ">
                            ${desktopScore > 0 ? desktopGrade + ' 등급' : '⏳ 준비중'}
                        </div>
                    </div>

                    <!-- Mobile 점수 -->
                    <div class="score-card mobile-card">
                        <div class="device-label mobile-label">📱 모바일</div>
                        <div class="score-number" style="color: ${mobileScore > 0 ? mobileColor : '#6b7280'};">
                            ${mobileScore > 0 ? mobileScore : '준비중'}
                        </div>
                        <div class="score-label mobile-label">성능 점수</div>
                        <div class="score-badge" style="
                            background-color: ${mobileScore > 0 ? mobileColor + '20' : '#f3f4f6'};
                            color: ${mobileScore > 0 ? mobileColor : '#6b7280'};
                        ">
                            ${mobileScore > 0 ? mobileGrade + ' 등급' : '⏳ 준비중'}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 페이지 용량 분석 섹션 -->
        <div class="section-container">
            <h3 class="section-title">📦 페이지 용량 분석</h3>
            <div class="page-size-grid">
                <!-- Desktop 용량 -->
                <div class="device-section desktop-section">
                    <h4 class="vitals-title desktop-label">🖥️ 데스크톱</h4>
                    <div class="size-item">
                        <span class="size-label">총 페이지 크기</span>
                        <span class="size-value">${formatBytes(pageSpeedData.desktop.pageSize?.totalSize || 0)}</span>
                    </div>
                    <div class="size-item">
                        <span class="size-label">리소스 개수</span>
                        <span class="size-value">${pageSpeedData.desktop.pageSize?.resourceCount || 0}개</span>
                    </div>
                    <div class="size-item">
                        <span class="size-label">JavaScript</span>
                        <span class="size-value">${formatBytes(
                          pageSpeedData.desktop.pageSize?.resourceBreakdown?.['script'] ||
                          pageSpeedData.desktop.pageSize?.resourceBreakdown?.['Script'] ||
                          pageSpeedData.desktop.pageSize?.resourceBreakdown?.['javascript'] ||
                          pageSpeedData.desktop.pageSize?.resourceBreakdown?.['JavaScript'] || 0
                        )}</span>
                    </div>
                    <div class="size-item">
                        <span class="size-label">이미지</span>
                        <span class="size-value">${formatBytes(
                          pageSpeedData.desktop.pageSize?.resourceBreakdown?.['image'] ||
                          pageSpeedData.desktop.pageSize?.resourceBreakdown?.['Image'] ||
                          pageSpeedData.desktop.pageSize?.resourceBreakdown?.['images'] ||
                          pageSpeedData.desktop.pageSize?.resourceBreakdown?.['Images'] || 0
                        )}</span>
                    </div>
                    <div class="size-item">
                        <span class="size-label">CSS</span>
                        <span class="size-value">${formatBytes(
                          pageSpeedData.desktop.pageSize?.resourceBreakdown?.['stylesheet'] ||
                          pageSpeedData.desktop.pageSize?.resourceBreakdown?.['Stylesheet'] ||
                          pageSpeedData.desktop.pageSize?.resourceBreakdown?.['css'] ||
                          pageSpeedData.desktop.pageSize?.resourceBreakdown?.['CSS'] || 0
                        )}</span>
                    </div>
                </div>

                <!-- Mobile 용량 -->
                <div class="device-section mobile-section">
                    <h4 class="vitals-title mobile-label">📱 모바일</h4>
                    <div class="size-item">
                        <span class="size-label">총 페이지 크기</span>
                        <span class="size-value">${formatBytes(pageSpeedData.mobile.pageSize?.totalSize || 0)}</span>
                    </div>
                    <div class="size-item">
                        <span class="size-label">리소스 개수</span>
                        <span class="size-value">${pageSpeedData.mobile.pageSize?.resourceCount || 0}개</span>
                    </div>
                    <div class="size-item">
                        <span class="size-label">JavaScript</span>
                        <span class="size-value">${formatBytes(
                          pageSpeedData.mobile.pageSize?.resourceBreakdown?.['script'] ||
                          pageSpeedData.mobile.pageSize?.resourceBreakdown?.['Script'] ||
                          pageSpeedData.mobile.pageSize?.resourceBreakdown?.['javascript'] ||
                          pageSpeedData.mobile.pageSize?.resourceBreakdown?.['JavaScript'] || 0
                        )}</span>
                    </div>
                    <div class="size-item">
                        <span class="size-label">이미지</span>
                        <span class="size-value">${formatBytes(
                          pageSpeedData.mobile.pageSize?.resourceBreakdown?.['image'] ||
                          pageSpeedData.mobile.pageSize?.resourceBreakdown?.['Image'] ||
                          pageSpeedData.mobile.pageSize?.resourceBreakdown?.['images'] ||
                          pageSpeedData.mobile.pageSize?.resourceBreakdown?.['Images'] || 0
                        )}</span>
                    </div>
                    <div class="size-item">
                        <span class="size-label">CSS</span>
                        <span class="size-value">${formatBytes(
                          pageSpeedData.mobile.pageSize?.resourceBreakdown?.['stylesheet'] ||
                          pageSpeedData.mobile.pageSize?.resourceBreakdown?.['Stylesheet'] ||
                          pageSpeedData.mobile.pageSize?.resourceBreakdown?.['css'] ||
                          pageSpeedData.mobile.pageSize?.resourceBreakdown?.['CSS'] || 0
                        )}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- 분석 총평 섹션 -->
        <div class="analysis-summary">
            <h3 class="analysis-title">
                📝 분석총평
            </h3>
            
            <!-- 참고사항 박스 -->
            <div style="
                margin-bottom: 25px; 
                padding: 20px; 
                background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%); 
                border-radius: 15px; 
                border: 1px solid rgba(245, 158, 11, 0.3)
            ">
                <p style="margin: 0; font-size: 16px; color: #92400e; line-height: 1.6; font-weight: 500;">
                    <strong>[참고]</strong> 웹사이트 성능 최적화 권장 기준: 페이지 용량 35MB 이하(PC 기준), HTTP 요청 수 50~60회 이하를 권장합니다. 
                    이 기준을 과도하게 초과할 경우 사용자 경험에 영향을 미쳐 이탈률 증가로 이어질 수 있습니다.
                </p>
            </div>
            
            <div class="analysis-content">
                ${llmAnalysis 
                  ? generateLLMAnalysisHTML(llmAnalysis)
                  : `<div style="
                      background-color: #fff8dc; 
                      padding: 20px; 
                      border-radius: 8px; 
                      font-size: 13px; 
                      color: #78350f; 
                      line-height: 1.5
                    ">
                      <strong>기본 성능 요약 (PageSpeed 데이터 기반):</strong><br />
                      데스크톱 성능 점수: ⏳ 준비중<br />
                      모바일 성능 점수: ⏳ 준비중<br />
                      첫 번째 콘텐츠 표시 시간: 데스크톱 ${safeToFixed(pageSpeedData.desktop.firstContentfulPaint, 1)}초, 모바일 ${safeToFixed(pageSpeedData.mobile.firstContentfulPaint, 1)}초<br />
                      <br />
                      <strong>일반적인 개선 권장사항:</strong><br />
                      • JavaScript 파일 압축 및 불필요한 코드 제거<br />
                      • 이미지 최적화 및 차세대 형식(WebP, AVIF) 활용<br />
                      • CSS 파일 압축 및 중요하지 않은 CSS 지연 로딩<br />
                      • 서버 응답 시간 단축 및 효율적인 캐싱 구현
                    </div>`
                }
            </div>
        </div>

        <!-- Core Web Vitals 섹션 -->
        <div class="section-container">
            <h3 class="section-title">⚡ 웹사이트 성능 지표 (Core Web Vitals)</h3>
            <p style="color: #4a5568; margin-bottom: 25px; line-height: 1.6;">
                Core Web Vitals는 Google이 제시하는 웹사이트의 핵심 성능 지표입니다. 실제 사용자가 경험하는 페이지 로딩 속도, 상호작용 응답성, 시각적 안정성을 측정합니다.
            </p>

            <div class="vitals-grid">
                <!-- Desktop Vitals -->
                <div class="vitals-section desktop-section">
                    <h4 class="vitals-title desktop-label">🖥️ 데스크톱 성능</h4>
                    
                    <div class="metric-item">
                        <div class="metric-label">🎨 첫 번째 콘텐츠 표시 시간 (FCP)</div>
                        <div class="metric-value" style="color: ${
                          (pageSpeedData.desktop.firstContentfulPaint || 0) <= 1.8 ? '#22c55e' : 
                          (pageSpeedData.desktop.firstContentfulPaint || 0) <= 3.0 ? '#f59e0b' : '#ef4444'
                        };">
                            ${safeToFixed(pageSpeedData.desktop.firstContentfulPaint, 1)}초
                        </div>
                        <div class="metric-status" style="
                            background-color: ${
                              (pageSpeedData.desktop.firstContentfulPaint || 0) <= 1.8 ? '#dcfce7' : 
                              (pageSpeedData.desktop.firstContentfulPaint || 0) <= 3.0 ? '#fef3c7' : '#fecaca'
                            };
                            color: ${
                              (pageSpeedData.desktop.firstContentfulPaint || 0) <= 1.8 ? '#15803d' : 
                              (pageSpeedData.desktop.firstContentfulPaint || 0) <= 3.0 ? '#a16207' : '#dc2626'
                            };
                        ">
                            ${(pageSpeedData.desktop.firstContentfulPaint || 0) <= 1.8 ? '좋음' : 
                              (pageSpeedData.desktop.firstContentfulPaint || 0) <= 3.0 ? '보통' : '개선 필요'}
                        </div>
                        <div style="font-size: 12px; color: #6b7280; margin-top: 8px;">
                            페이지 콘텐츠가 처음 렌더링되는 시점까지의 시간<br>
                            데스크톱 권장: 1.8초 이하 (우수), 3.0초 이하 (보통)
                        </div>
                    </div>

                    ${pageSpeedData.desktop.totalBlockingTime !== undefined ? `
                    <div class="metric-item">
                        <div class="metric-label">🚫 총 차단 시간 (TBT)</div>
                        <div class="metric-value" style="color: ${
                          (pageSpeedData.desktop.totalBlockingTime || 0) <= 200 ? '#22c55e' : 
                          (pageSpeedData.desktop.totalBlockingTime || 0) <= 600 ? '#f59e0b' : '#ef4444'
                        };">
                            ${safeToFixed(pageSpeedData.desktop.totalBlockingTime, 0)}ms
                        </div>
                        <div class="metric-status" style="
                            background-color: ${
                              (pageSpeedData.desktop.totalBlockingTime || 0) <= 200 ? '#dcfce7' : 
                              (pageSpeedData.desktop.totalBlockingTime || 0) <= 600 ? '#fef3c7' : '#fecaca'
                            };
                            color: ${
                              (pageSpeedData.desktop.totalBlockingTime || 0) <= 200 ? '#15803d' : 
                              (pageSpeedData.desktop.totalBlockingTime || 0) <= 600 ? '#a16207' : '#dc2626'
                            };
                        ">
                            ${(pageSpeedData.desktop.totalBlockingTime || 0) <= 200 ? '좋음' : 
                              (pageSpeedData.desktop.totalBlockingTime || 0) <= 600 ? '보통' : '개선 필요'}
                        </div>
                        <div style="font-size: 12px; color: #6b7280; margin-top: 8px;">
                            메인 스레드가 차단되어 입력 응답을 방해하는 총 시간<br>
                            데스크톱 권장: 200ms 이하 (우수), 600ms 이하 (보통)
                        </div>
                    </div>
                    ` : ''}
                </div>

                <!-- Mobile Vitals -->
                <div class="vitals-section mobile-section">
                    <h4 class="vitals-title mobile-label">📱 모바일 성능</h4>
                    
                    <div class="metric-item">
                        <div class="metric-label">🎨 첫 번째 콘텐츠 표시 시간 (FCP)</div>
                        <div class="metric-value" style="color: ${
                          (pageSpeedData.mobile.firstContentfulPaint || 0) <= 2.0 ? '#22c55e' : 
                          (pageSpeedData.mobile.firstContentfulPaint || 0) <= 4.0 ? '#f59e0b' : '#ef4444'
                        };">
                            ${safeToFixed(pageSpeedData.mobile.firstContentfulPaint, 1)}초
                        </div>
                        <div class="metric-status" style="
                            background-color: ${
                              (pageSpeedData.mobile.firstContentfulPaint || 0) <= 2.0 ? '#dcfce7' : 
                              (pageSpeedData.mobile.firstContentfulPaint || 0) <= 4.0 ? '#fef3c7' : '#fecaca'
                            };
                            color: ${
                              (pageSpeedData.mobile.firstContentfulPaint || 0) <= 2.0 ? '#15803d' : 
                              (pageSpeedData.mobile.firstContentfulPaint || 0) <= 4.0 ? '#a16207' : '#dc2626'
                            };
                        ">
                            ${(pageSpeedData.mobile.firstContentfulPaint || 0) <= 2.0 ? '좋음' : 
                              (pageSpeedData.mobile.firstContentfulPaint || 0) <= 4.0 ? '보통' : '개선 필요'}
                        </div>
                        <div style="font-size: 12px; color: #6b7280; margin-top: 8px;">
                            페이지 콘텐츠가 처음 렌더링되는 시점까지의 시간<br>
                            모바일 권장: 2.0초 이하 (우수), 4.0초 이하 (보통)
                        </div>
                    </div>

                    ${pageSpeedData.mobile.totalBlockingTime !== undefined ? `
                    <div class="metric-item">
                        <div class="metric-label">🚫 총 차단 시간 (TBT)</div>
                        <div class="metric-value" style="color: ${
                          (pageSpeedData.mobile.totalBlockingTime || 0) <= 300 ? '#22c55e' : 
                          (pageSpeedData.mobile.totalBlockingTime || 0) <= 900 ? '#f59e0b' : '#ef4444'
                        };">
                            ${safeToFixed(pageSpeedData.mobile.totalBlockingTime, 0)}ms
                        </div>
                        <div class="metric-status" style="
                            background-color: ${
                              (pageSpeedData.mobile.totalBlockingTime || 0) <= 300 ? '#dcfce7' : 
                              (pageSpeedData.mobile.totalBlockingTime || 0) <= 900 ? '#fef3c7' : '#fecaca'
                            };
                            color: ${
                              (pageSpeedData.mobile.totalBlockingTime || 0) <= 300 ? '#15803d' : 
                              (pageSpeedData.mobile.totalBlockingTime || 0) <= 900 ? '#a16207' : '#dc2626'
                            };
                        ">
                            ${(pageSpeedData.mobile.totalBlockingTime || 0) <= 300 ? '좋음' : 
                              (pageSpeedData.mobile.totalBlockingTime || 0) <= 900 ? '보통' : '개선 필요'}
                        </div>
                        <div style="font-size: 12px; color: #6b7280; margin-top: 8px;">
                            메인 스레드가 차단되어 입력 응답을 방해하는 총 시간<br>
                            모바일 권장: 300ms 이하 (우수), 900ms 이하 (보통)
                        </div>
                    </div>
                    ` : ''}
                </div>
            </div>
        </div>

        <!-- 성능 개선 기회 - 데스크톱 -->
        ${pageSpeedData.desktop.opportunities && pageSpeedData.desktop.opportunities.length > 0 ? (() => {
          const desktopOpportunities = getImportantOpportunities(pageSpeedData.desktop.opportunities);
          if (desktopOpportunities.length === 0) return '';
          
          return `
          <div class="section-container">
              <h3 class="section-title">💡 핵심 성능 개선 기회 (🖥️ 데스크톱)</h3>
              <div class="opportunities">
                  ${desktopOpportunities.slice(0, 5).map((opportunity: any, index: number) => {
                    let resourcesHtml = '';
                    if (opportunity.details && opportunity.details.items && opportunity.details.items.length > 0) {
                      const excludePatterns = ['optimizer.php'];
                      const filteredItems = opportunity.details.items.filter((item: any) => {
                        const url = item.url || item.source || item.node?.snippet || '';
                        return !excludePatterns.some(pattern => url.includes(pattern));
                      });
                      
                      if (filteredItems.length > 0) {
                        resourcesHtml = '<div class="resources-list">';
                        filteredItems.slice(0, 5).forEach((item: any) => {
                          const fullUrl = item.url ? item.url.replace(/^https?:\/\//, '') : item.source || item.node?.snippet || '알 수 없음';
                          const displayUrl = fullUrl.length > 120 ? fullUrl.substring(0, 120) + '...' : fullUrl;
                          resourcesHtml += `
                            <div class="resource-item">
                                <div style="display: flex; align-items: center; gap: 8px; min-width: 0;">
                                    <a href="${item.url || `https://${fullUrl}`}" target="_blank" rel="noopener noreferrer" 
                                       class="resource-link"
                                       title="클릭하여 ${fullUrl} 방문">
                                        ${displayUrl}
                                    </a>
                                </div>
                            </div>`;
                        });
                        resourcesHtml += '</div>';
                      }
                    }
                    
                    return `
                    <div class="opportunity-item">
                        <div class="opportunity-title">${index + 1}. ${translateOpportunityTitle(opportunity.title)}</div>
                        <div class="opportunity-description">${translateDescription(opportunity.description)}</div>
                        ${resourcesHtml}
                    </div>`;
                  }).join('')}
              </div>
          </div>`;
        })() : ''}

        <!-- 성능 개선 기회 - 모바일 -->
        ${pageSpeedData.mobile.opportunities && pageSpeedData.mobile.opportunities.length > 0 ? (() => {
          const mobileOpportunities = getImportantOpportunities(pageSpeedData.mobile.opportunities);
          if (mobileOpportunities.length === 0) return '';
          
          return `
          <div class="section-container">
              <h3 class="section-title">💡 핵심 성능 개선 기회 (📱 모바일)</h3>
              <div class="opportunities">
                  ${mobileOpportunities.slice(0, 5).map((opportunity: any, index: number) => {
                    let resourcesHtml = '';
                    if (opportunity.details && opportunity.details.items && opportunity.details.items.length > 0) {
                      const excludePatterns = ['optimizer.php'];
                      const filteredItems = opportunity.details.items.filter((item: any) => {
                        const url = item.url || item.source || item.node?.snippet || '';
                        return !excludePatterns.some(pattern => url.includes(pattern));
                      });
                      
                      if (filteredItems.length > 0) {
                        resourcesHtml = '<div class="resources-list">';
                        filteredItems.slice(0, 5).forEach((item: any) => {
                          const fullUrl = item.url ? item.url.replace(/^https?:\/\//, '') : item.source || item.node?.snippet || '알 수 없음';
                          const displayUrl = fullUrl.length > 120 ? fullUrl.substring(0, 120) + '...' : fullUrl;
                          resourcesHtml += `
                            <div class="resource-item">
                                <div style="display: flex; align-items: center; gap: 8px; min-width: 0;">
                                    <a href="${item.url || `https://${fullUrl}`}" target="_blank" rel="noopener noreferrer" 
                                       class="resource-link"
                                       title="클릭하여 ${fullUrl} 방문">
                                        ${displayUrl}
                                    </a>
                                </div>
                            </div>`;
                        });
                        resourcesHtml += '</div>';
                      }
                    }
                    
                    return `
                    <div class="opportunity-item">
                        <div class="opportunity-title">${index + 1}. ${translateOpportunityTitle(opportunity.title)}</div>
                        <div class="opportunity-description">${translateDescription(opportunity.description)}</div>
                        ${resourcesHtml}
                    </div>`;
                  }).join('')}
              </div>
          </div>`;
        })() : ''}

        <!-- 마무리 -->
        <div class="section-container" style="text-align: center; color: #6b7280; font-style: italic;">
            <p>이 보고서는 ${new Date().toLocaleString('ko-KR')}에 생성되었습니다.</p>
        </div>
    </div>
</body>
</html>`;

  return html;
};